import React from 'react'
import styled from 'styled-components'
import { Tooltip } from '../tooltip'
import { Icon } from '../icon'
import { StatCardProps } from './stat-card.types'

const StyledStatCard = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.sizes.s};
    border-radius: ${theme.borderRadii.xlarge};
    background-color: ${theme.colors.white};
    border: 2px solid ${theme.colors.primarySurface};
    padding: ${theme.sizes.s};
    min-width: 0;
    flex: 1;
  `}
`

// Figma node 5703:3564 — Label/Value/Subtitle line-heights are sourced from
// these single-source constants so the Header `min-height` floor (sum of
// the three) stays in sync with any future typography tweak.
const LABEL_LINE_HEIGHT_PX = 20
const VALUE_LINE_HEIGHT_PX = 32
const SUBTITLE_LINE_HEIGHT_PX = 16

// Header stacks Label / Value / Subtitle without internal gap so the hero
// metric reads as a tight typographic block, while the 1rem container `gap`
// on StyledStatCard separates Header from the chart slot.
//
// `min-height` reserves the full 3-line layout regardless of which slots a
// card actually fills. Per Figma node 5703:3564 every card section is
// fixed-dimension so the chart and legend land at the same y across cards
// — without the floor, cards with only a label would shift the chart
// upward and break legend alignment.
const Header = styled.div`
  display: flex;
  flex-direction: column;
  min-height: ${LABEL_LINE_HEIGHT_PX +
  VALUE_LINE_HEIGHT_PX +
  SUBTITLE_LINE_HEIGHT_PX}px;
`

// The label and trend badge share this row. The badge is `flex-shrink: 0` (see
// TrendBadge) and the label ellipsis-truncates (see Label), so when a trend
// appears (e.g. once a date range is selected) the label gives up width instead
// of wrapping onto a second line — the row stays one line tall and the card never
// grows between "All time" (no trend) and a specific range (NEX-755). The full
// number stays visible in the badge; the full label is available via its `title`.
const HeaderRow = styled.div`
  ${({ theme }) => `
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${theme.sizes.xs};
  `}
`

// Figma node 5703:3564 spec — Subtitle/Helper colour = content/tertiary, via the
// `contentTertiary` semantic token.
// Ellipsis-truncate on a single line rather than wrap, so a long label (or the
// trend badge beside it) never pushes the label onto a second line and grows the
// card (NEX-755). `min-width: 0` lets it shrink inside the flex row; the full
// text stays available via the `title` attribute set on render.
const Label = styled.span`
  ${({ theme }) => `
    font-size: ${theme.fontSizes.bodySmall};
    font-weight: ${theme.fontWeights.medium};
    line-height: ${LABEL_LINE_HEIGHT_PX}px;
    color: ${theme.semantic.contentTertiary};
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`

// Groups the label with its optional info icon so they read as one unit on the
// left of the row, leaving the trend badge pinned to the right. Pinned to one
// label line tall so the info icon centres on the label text. `min-width: 0`
// lets the group shrink so the Label inside can ellipsis-truncate when the badge
// leaves it a tight row.
const LabelGroup = styled.div`
  ${({ theme }) => `
    display: flex;
    align-items: center;
    gap: ${theme.sizes.xxs};
    min-width: 0;
  `}
`

// Unstyled, keyboard-focusable trigger so the description tooltip is reachable
// without a mouse; the line-height box keeps the icon aligned to the label text.
// `flex-shrink: 0` so the icon stays intact and the Label truncates first.
const InfoButton = styled.button`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: help;
  line-height: 0;
`

const Value = styled.span`
  ${({ theme }) => `
    font-size: ${theme.fontSizes.h2};
    font-weight: ${theme.fontWeights.semiBold};
    line-height: ${VALUE_LINE_HEIGHT_PX}px;
    color: ${theme.colors.text_primary};
  `}
`

const Subtitle = styled.span`
  ${({ theme }) => `
    font-size: ${theme.fontSizes.caption};
    line-height: ${SUBTITLE_LINE_HEIGHT_PX}px;
    color: ${theme.semantic.contentTertiary};
  `}
`

// Pin the badge box to exactly one Label line tall so a trend never grows the
// HeaderRow past the Header `min-height` floor — otherwise a card showing a trend
// would push its chart down and break the chart/legend y-alignment across cards.
// A fixed `height` + `box-sizing: border-box` + centred content makes this
// independent of the badge's padding/line-height (no padding-coupled arithmetic).
// `flex-shrink: 0` keeps the full number intact and forces the Label — not the
// badge — to ellipsize when the row is tight; `white-space: nowrap` keeps the
// arrow and value on one line.
const TrendBadge = styled.span<{ $direction: 'up' | 'down' }>`
  ${({ theme, $direction }) => `
    display: inline-flex;
    align-items: center;
    box-sizing: border-box;
    flex-shrink: 0;
    height: ${LABEL_LINE_HEIGHT_PX}px;
    gap: ${theme.sizes.xxs};
    font-size: ${theme.fontSizes.caption};
    font-weight: ${theme.fontWeights.medium};
    color: ${
      $direction === 'up' ? theme.colors.success400 : theme.colors.alert400
    };
    background-color: ${
      $direction === 'up' ? theme.colors.success100 : theme.colors.alert100
    };
    padding: 0 ${theme.sizes.xs};
    border-radius: ${theme.borderRadii.pill};
    white-space: nowrap;
  `}
`

// Natural-sized — the chart inside sets its own fixed height (CHART_HEIGHT,
// 240px). Don't `flex: 1` here or a stretched card would inflate the slot and
// drift the chart away from the header; any extra height should fall below
// the legend instead.
const ChartContainer = styled.div`
  min-height: 0;
`

export const StatCard = ({
  label,
  value,
  subtitle,
  description,
  trend,
  children,
  legend,
}: Readonly<StatCardProps>) => {
  // Built once so the tooltip / no-tooltip paths share identical badge markup \u2014
  // the tooltip branch just wraps the same element.
  const trendBadge = trend ? (
    <TrendBadge $direction={trend.direction}>
      {trend.direction === 'up' ? '\u2191' : '\u2193'}
      {trend.value ? ` ${trend.value}` : ''}
    </TrendBadge>
  ) : null

  return (
    <StyledStatCard>
      <Header>
        <HeaderRow>
          <LabelGroup>
            <Label title={label}>{label}</Label>
            {description && (
              <Tooltip content={description}>
                <InfoButton type="button" aria-label={description}>
                  <Icon name="info" size={4} color="gray400" />
                </InfoButton>
              </Tooltip>
            )}
          </LabelGroup>
          {trend?.tooltip ? (
            <Tooltip content={trend.tooltip}>{trendBadge}</Tooltip>
          ) : (
            trendBadge
          )}
        </HeaderRow>
        {value !== undefined && <Value>{value}</Value>}
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </Header>
      {children && <ChartContainer>{children}</ChartContainer>}
      {legend}
    </StyledStatCard>
  )
}
